// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { toast } from 'react-toastify';

import Sortable from '../Sortable';

// Modals
import DeactivateAccount from '../Modals/DeactivateAccount';
import AddPaymentMethod from '../Modals/AddPaymentMethod';

// Utilities
import formatDate from '../../utilities/formatDate';
import capitalize from '../../utilities/capitalize';

import api from '../../api';

import '../../styles/files.css';
import '../../styles/sortable.css';
import '../../styles/billing.css';
export default class Billing extends React.Component {

    state = {
        methods: [],
        default: null,
        invoices: [],
        upcoming: {
            amount: 0,
            due: new Date(),
            overage: 0,
            bulk: 0,
            automate: [],
            workflows: []
        },
        usage: {
            usage: 0,
            storage: 0,
            archived: 0
        },

        // Loading individual items
        loading: {
            methods: true,
            invoices: true,
            usage: true
        },

        modal: "",
        modalData: {}
    }

    componentDidMount = async () => {
        this.loadPaymentMethods();
        this.loadPastInvoices();
        // this.loadUsageSummary();
    }

    loadPaymentMethods = async () => {
        const res = await api.get('/team/billing/method/list');
        if (!res || !res.methods) {
            toast.error("Failed to load payment methods.");
            return this.setState({ loading: { ...this.state.loading, methods: false } });
        }
        await this.setState({
            methods: res.methods,
            default: res.selected,
            loading: {
                ...this.state.loading,
                methods: false
            }
        });
    }

    loadPastInvoices = async () => {
        const res = await api.get('/team/billing/invoice/list');
        if (!res || !res.invoices) {
            toast.error("Failed to load past invoices.");
            return this.setState({ loading: { ...this.state.loading, invoices: false } });
        }
        await this.setState({
            invoices: res.invoices,
            loading: {
                ...this.state.loading,
                invoices: false
            }
        });
    }

    loadUsageSummary = async () => {
        const res = await api.get('/team/usage');
        if (!res || !res.upcoming) {
            toast.error("Failed to load usage summary.");
            return this.setState({ loading: { ...this.state.loading, usage: false } });
        }
        await this.setState({
            upcoming: res.upcoming,
            usage: {
                usage: res.usage,
                storage: res.storage,
                archived: res.archived
            },
            loading: {
                ...this.state.loading,
                usage: false
            }
        });
    }

    setDefaultPaymentMethod = async (methodId) => {
        await this.props.loading();
        const res = await api.post('/team/billing/method/select', { methodId });
        if (res && res.success)
            this.setState({ default: methodId });
        this.props.loaded();
    }

    addCreditCard = async (card, details) => {
        const res = await api.post('/team/billing/method/create', {
            type: "card",
            method: {
                card: {
                    number: card.number,
                    exp_month: card.expiry.substr(0,2),
                    exp_year: card.expiry.substr(3),
                    cvc: card.cvc
                },
                billing_details: {
                    ...details,
                    email: this.props.user.email
                }
            }
        });
        if (!res || !res.success || !res.method) return false;

        toast.success("Successfully added new credit card!");

        // Add locally
        this.setState({
            methods: [ ...this.state.methods, res.method ]
        });

        return true;
    }

    removePaymentMethod = async (methodId) => {
        await this.props.loading();
        const res = await api.post('/team/billing/method/remove', { methodId });
        // Remove locally
        if (res && res.success) {
            let methods = [ ...this.state.methods ];
            const methodIdx = methods.findIndex(m => m.id === methodId);
            methods.splice(methodIdx,1);
            this.setState({ methods });
        }
        this.props.loaded();
    }

    activatePlan = async () => {
        await this.props.loading();
        await api.post('/team/billing/plan/activate');
        window.location.href = "/settings/billing"; // Reload
        this.props.loaded();
    }

    modal = (modal="", modalData={}) => this.setState({ modal, modalData })

    renderUsageMeta = (cost, qty, total, notes="") => {
        const nullHandler = (val) => val === null ? <span className="billing-na">-</span> : val;
        return (<div className="stack-list-meta">
            <div className="stack-list-meta-col document-list-meta-col billing-meta sortable-list-header-hide-small billing-meta-notes" style={{ width: 220 }}>
                {nullHandler(notes)}
            </div>
            <div className="stack-list-meta-col document-list-meta-col billing-meta billing-amount" style={{ width: 140 }}>
                {nullHandler(cost)}
            </div>
            <div className="stack-list-meta-col document-list-meta-col billing-meta billing-amount" style={{ width: 140 }}>
                {nullHandler(qty)}
            </div>
            <div className="stack-list-meta-col document-list-meta-col billing-meta billing-amount" style={{ width: 126 }}>
                {nullHandler(total)}
            </div>
        </div>);
    }

    render = () => {
        
        const billing = this.props.team && this.props.team.billing ? this.props.team.billing : {
            plan: {
                tier: "Personal",
                limits: {},
            },
            activated: false,
            usage: 0,
            reported: 0
        };
        
        const isEnterprise = billing.plan.tier === "Enterprise";
        
        let unpaid = null;
        for (const inv of this.state.invoices)
            if (inv.paid < inv.due) {
                unpaid = inv;
                break;
            }
        
        return (<div className="settings-content" style={{ minWidth: 1000 }}>

            {unpaid?.url? <div className="billing-alert">
                There was a problem collecting a payment for <a href={unpaid.url} target="_blank" rel="noopener noreferrer">Invoice #{unpaid.id}</a>. You have a balance of <strong>${((unpaid.due - unpaid.paid)/100).toFixed(2)}</strong> remaining.<br />
                Please <a href={unpaid.url} target="_blank" rel="noopener noreferrer">click here</a> to take care of the remaining balance immediately to avoid permanent deletion of your Stax·ai account and all associated data.
            </div> : ""}

            {/* Activate plan? */}
            {/* {!billing.activated ? <div className="billing-activate">
                <h1>Unleash your <span className="billing-activate-emphasis">full</span> potential</h1>
                <p>
                    You are currently on a <strong>limited trial</strong> of Stax·ai. Activate your account to unlock its full functionality. Streamline your document workflow using <strong>Automate</strong> pipelines,upload as many files as you want, access Stax·ai's core functionality through the <strong>Developer API</strong>, sync your Dropbox or Google Drive accounts, and so much more!
                </p>
                <div
                    className="billing-activate-button"
                    onClick={this.activatePlan}
                >Activate my Account</div>
                <div style={{ clear: 'both' }} />
            </div> : ""} */}

            {/* <h2 style={{ paddingTop: 10 }}>Current Billing Cycle</h2>
            {this.state.loading.usage ? <Loading
                type="bubbles"
                color="var(--color-primary-dark)"
                height={80}
                width={80}
            /> : <>
                <div className="sortable-list">
                    <div
                        className="stack-list-header"
                        style={{ minWidth: 900 }}
                    >
                        <div
                            className="stack-list-header-left"
                            style={{ paddingLeft: 20 }}
                        >Item Description</div>

                        <div className="stack-list-meta">
                            <div
                                className="stack-list-header-item sortable-list-header-hide-small"
                                style={{ width: 220 }}
                            ></div>
                            <div
                                className="stack-list-header-item billing-amount"
                                style={{ width: 140 }}
                            >Unit Cost</div>
                            <div
                                className="stack-list-header-item billing-amount"
                                style={{ width: 140 }}
                            >Quantity</div>
                            <div
                                className="stack-list-header-item billing-amount-header"
                                style={{ width: 128 }}
                            >Line Total</div>
                        </div>
                    </div>
                    
                    <div className="billing-usage-group">
                        {isEnterprise? <>
                            <div className="billing-usage-description">Stax·ai Enterprise Plan - as per Service Level Agreement</div>
                            {this.renderUsageMeta(
                                <span className="billing-na">See SLA</span>,
                                null,
                                <span className="billing-na">See SLA</span>
                            )}
                        </> : <>
                            <div className="billing-usage-description">Stax·ai {billing.plan.tier} Plan - Monthly Subscription</div>
                            {this.renderUsageMeta(
                                billing.plan.tier === "Pro" ? "$"+( 59 ).toFixed(2) : billing.plan.tier === "TPA" ? "See SLA":  "$"+( 19 ).toFixed(2),
                                billing.plan.tier === "Pro" ? String(numUsers) + plural(" user", numUsers) : "-",
                                billing.plan.tier === "Pro" ? "$"+(( 59 ) * numUsers).toFixed(2) : billing.plan.tier === "TPA" ? "See SLA": "$"+(( 19 ) * numUsers).toFixed(2)
                            )}
                        </>}

                        {isEnterprise ? <>
                            <div className="billing-usage-group-item">
                                <div className="billing-usage-item-description">
                                    Included User Accounts
                                </div>
                                {this.renderUsageMeta(null, numUsers, null, billing.plan.limits.maxUsers + " seats included")}
                            </div>
                        </> : ""}

                        <div className="billing-usage-group-item">
                            <div className="billing-usage-item-description">
                                Included Document Intake & Processing
                            </div>
                            {this.renderUsageMeta(null, Math.min(usage.usage, maxUsage), null, formatNumber(maxUsage) + " pages included")}
                        </div>

                        <div className="billing-usage-group-item">
                            <div className="billing-usage-item-description">
                                Included Document Storage for Quick Access
                            </div>
                            {this.renderUsageMeta(null, usage.storage, null, formatNumber(isEnterprise ? billing.plan.limits.maxStorage : billing.plan.tier === "Pro" ? numUsers * 25000 : 10000) + " pages included")}
                        </div>

                        <div className="billing-usage-group-item">
                            <div className="billing-usage-item-description">
                                Included Document Archival
                            </div>
                            {this.renderUsageMeta(null, usage.archived, null, formatNumber(isEnterprise ? billing.plan.limits.maxArchive : billing.plan.tier === "Pro" ? numUsers * 1000000 : 500000) + " pages included")}
                        </div>

                        <div style={{ clear: 'both' }} />
                    </div>

                    <div className="billing-usage-group">
                        <div className="billing-usage-description">
                            Document Processing Overages <span style={{ fontWeight: 300, color: "rgb(200,200,200)", fontSize: 15 }}>(pages over limit)</span>
                        </div>
                        {this.renderUsageMeta(
                            "5¢",
                            upcoming.overage,
                            "$" + (upcoming.overage * 0.05).toFixed(2)
                        )}

                        <div style={{ clear: 'both' }} />
                    </div>

                    <div className="billing-usage-group">
                        <div className="billing-usage-description">
                            Bulk Document Processing or Import
                        </div>
                        {this.renderUsageMeta(
                            null,
                            upcoming.bulk,
                            "$" + (upcoming.bulk * 0.025).toFixed(2),
                            "Starts at 2.5¢/page"
                        )}

                        <div style={{ clear: 'both' }} />
                    </div>

                    {upcoming.automate && upcoming.automate.length > 0 ? <div className="billing-usage-group">
                        <div className="billing-usage-description">Stax·ai Automate Pipelines - Module Usage</div>
                        {this.renderUsageMeta( null, null, null )}

                        {upcoming.automate.map((m,idx) => <div
                            key={idx}
                            className="billing-usage-group-item"
                        >
                            <div className="billing-usage-item-description">
                                Module: {m.name}
                            </div>
                            {this.renderUsageMeta(
                                m.unit_cost === 0 ? null : m.unit_cost >= 1 ? "$" + m.unit_cost.toFixed(2) : (m.unit_cost*100) + "¢",
                                m.quantity,
                                "$" + (m.unit_cost * m.quantity).toFixed(2),
                                m.unit_cost > 0 ? "" : "Free to use"
                            )}
                        </div>)}

                        <div style={{ clear: 'both' }} />
                    </div> : ""}

                    {upcoming.workflows && upcoming.workflows.length > 0 ? <div className="billing-usage-group">
                        <div className="billing-usage-description">Stax·ai Workflows</div>
                        {this.renderUsageMeta( null, null, null )}

                        {upcoming.workflows.map((m,idx) => <div
                            key={idx}
                            className="billing-usage-group-item"
                        >
                            <div className="billing-usage-item-description">
                                Workflow: {m.name}
                            </div>
                            {this.renderUsageMeta(
                                m.unit_cost === 0 ? null : m.unit_cost >= 1 ? "$" + m.unit_cost.toFixed(2) : (m.unit_cost*100) + "¢",
                                m.quantity,
                                "$" + (m.unit_cost * m.quantity).toFixed(2),
                                m.unit_cost > 0 ? "" : "Free to use"
                            )}
                        </div>)}

                        <div style={{ clear: 'both' }} />
                    </div> : ""}

                    {isEnterprise ? <div className="billing-note">
                        Your team is on an <strong>Enterprise</strong> or <strong>Legacy</strong> account. Invoice total is based on an <strong>SLA</strong> in which you may have a fixed price, discounts, or other factors.
                    </div> : ""}

                    <div className="billing-total">
                        <div className="billing-total-line">
                            <div className="billing-total-title">Invoice Total</div>
                            <div className="billing-total-value billing-amount">
                                ${upcoming.amount.toFixed(2)}
                            </div>
                        </div>
                        <div className="billing-total-line">
                            <div className="billing-total-title">Total due on</div>
                            <div className="billing-total-value billing-amount" style={{ fontWeight: 400 }}>
                                {formatDate(upcoming.due, false, true)}
                            </div>
                        </div>
                    </div>
                    <div style={{ clear: 'both' }} />
                </div>
            </>} */}

<h2>Payment Methods
                <div className="settings-add" onClick={() => this.modal("add-payment-method")}>＋ Add new payment method</div>
            </h2>
            {this.state.loading.methods ? <Loading
                type="bubbles"
                color="var(--color-primary-dark)"
                height={80}
                width={80}
            /> : <>
                <Sortable
                    title=""
                    data={this.state.methods.map(m => {
                        if (m.type === "card") {
                            return ({
                                id: m.id,
                                title: m.card.country + " " + capitalize(m.card.brand) + " " + m.card.funding + " card ending in " + m.card.last4,
                                brand: m.card.brand,
                                addedOn: new Date(m.created * 1000),
                                expiry: (m.card.exp_month < 10 ? "0" + String(m.card.exp_month) : m.card.exp_month) + " / " + m.card.exp_year,
                                checkZip: m.card.checks.address_postal_code_check,
                                checkCVC: m.card.checks.cvc_check
                            });
                        }

                        return ({});
                    })}

                    // Default sort
                    defaultSortBy="date"
                    defaultSortDir={-1}

                    // Header configuration
                    header={{
                        name: {
                            prop: "title",
                            label: "Description"
                        },
                        meta: [
                            {
                                prop: "addedOn",
                                label: "Added on",
                                width: 140
                            },
                            {
                                prop: "expiry",
                                label: "Expires on",
                                width: 140,
                            },
                            {
                                prop: "due",
                                label: "Actions",
                                width: 220,
                                classes: "billing-amount-header"
                            },
                        ]
                    }}

                    // Line item renderer
                    renderItem={(m, idx, renderMeta) => {
                        return <div
                            className="sortable-list-item billing-card"
                            key={idx}
                            style={{
                                backgroundImage: "url('https://static.stax.ai/images/payment/" + m.brand + ".png')" 
                            }}
                        >
                            <div className="sortable-list-name sortable-list-study-name billing-card-description" style={{ maxWidth: 365 }}>
                                {m.title}
                                {m.checkZip === "fail" || m.checkCVC === "fail" ? <div className="billing-failed-card">
                                    This credit card failed the verification process. Please check the expiry date, CVC & zip code and replace this card.
                                </div> : ""}
                            </div>
        
                            <div className="stack-list-meta">
                                {renderMeta(formatDate(m.addedOn), 140)}
                                {renderMeta(m.expiry, 140)}
                                {renderMeta(this.state.default !== m.id ? <>
                                    <div
                                        className="settings-email-right settings-email-delete"
                                        title="Remove payment method"
                                        style={{
                                            width: 62,
                                            marginRight: 0
                                        }}
                                        onClick={() => this.removePaymentMethod(m.id)}
                                    >Remove</div>

                                    <div
                                        className="settings-email-right settings-email-retry"
                                        style={{
                                            backgroundImage: "url('https://static.stax.ai/images/icons/action-check.png')",
                                            width: 83,
                                            marginLeft: 0
                                        }}
                                        title="Set as default payment method"
                                        onClick={() => this.setDefaultPaymentMethod(m.id)}
                                    >Set Default</div>
                                </> : <div
                                    style={{
                                        fontSize: 15,
                                        color: "rgb(200,200,200)",
                                        height: 45,
                                        lineHeight: '45px'
                                    }}
                                >(Default payment method)</div>, 220, "billing-amount")}
                            </div>
                            <div style={{ clear: 'both' }} />
                        </div>;
                    }}

                />
                <div style={{ clear: 'both', height: 20  }} />
            </>}

            <h2>Past Invoices</h2>
            {this.state.loading.invoices ? <Loading
                type="bubbles"
                color="var(--color-primary-dark)"
                height={80}
                width={80}
            /> : <>
                <Sortable
                    title=""
                    data={this.state.invoices}

                    // Default sort
                    defaultSortBy="date"
                    defaultSortDir={-1}

                    // Header configuration
                    header={{
                        name: {
                            prop: "id",
                            label: "Invoice ID"
                        },
                        meta: [
                            {
                                prop: "date",
                                label: "Invoice Date",
                                width: 200
                            },
                            {
                                prop: "paid",
                                label: "Paid Amount",
                                width: 140,
                                classes: "billing-amount"
                            },
                            {
                                prop: "due",
                                label: "Due Amount",
                                width: 140,
                                classes: "billing-amount-header"
                            },
                        ]
                    }}

                    // Line item renderer
                    renderItem={(inv, idx, renderMeta) => {
                        return <div
                            className="sortable-list-item"
                            key={idx}
                            style={{
                                backgroundImage: "url('https://static.stax.ai/images/icons/invoice" + (inv.paid >= inv.due ? "-paid" : "") + ".png')" 
                            }}
                            onClick={() => window.open(inv.url, '_blank')}
                        >
                            <div className="sortable-list-name sortable-list-study-name" style={{ maxWidth: 360 }}>
                                Invoice #{inv.id}
                            </div>
        
                            <div className="stack-list-meta">
                                {renderMeta(new Date(Date.parse(inv.date)).toDateString(), 200)}
                                {renderMeta(<>
                                    <img
                                        src={"https://static.stax.ai/images/icons/" + (inv.paid === inv.due ? "reviewed" : "error") + ".png"}
                                        alt="[Paid]"
                                        style={{ marginTop: 15 }}
                                    />
                                    <span style={inv.paid === inv.due ? {} : { fontWeight: 800 }}>${(inv.paid/100).toFixed(2)}</span>
                                </>, 140, "billing-amount")}
                                {renderMeta("$" + (inv.due/100).toFixed(2), 144, "billing-amount")}
                            </div>
                        </div>;
                    }}

                />
                <div style={{ clear: 'both', height: 20  }} />
            </>}

            <div style={{ clear: 'both', height: 20  }} />

            
            <h2>Delete account</h2>
            {isEnterprise ? <p style={{ marginTop: -20, color: 'rgb(160,160,160)', fontSize: 15, fontStyle: 'italic' }}>
                Your team is either on a <strong>Enterprise</strong> or <strong>Legacy</strong> Stax·ai account. To deactivate your account, please contact your <a href="mailto:lindsay.s@staxai">Customer Success Representative</a>.
            </p> : this.props.user && this.props.team && this.props.user._id === this.props.team.owner ? <>
                <p>
                    If you deactivate your account, all your team users, documents, stacks, and any other data associated with your team on Stax·ai will be <strong>permanently</strong> deleted and you will lose access to your Stax·ai account. Please reconsider before doing this. If you don't believe Stax·ai is working for you, please reach out to your <a href="mailto:lindsay.s@staxai">Customer Success Representative</a> for assistance on making the best of Stax·ai.
                    <br /><br />
                    <span
                        className="settings-link"
                        onClick={() => this.modal("deactivate-account")}
                    >Click here</span> to permanently delete your Stax·ai account.
                </p>
            </> : <p style={{ marginTop: -20, color: 'rgb(160,160,160)', fontSize: 15, fontStyle: 'italic' }}>
                Only the team owner can deactivate the account. Please contact the user who created your team.
            </p>}

            {/* Modals */}
            {this.state.modal === "deactivate-account" ? <DeactivateAccount
                modal={this.modal}
                loading={this.props.loading}
                loaded={this.props.loaded}
            /> :
            
            this.state.modal === "add-payment-method" ? <AddPaymentMethod
                modal={this.modal}
                loading={this.props.loading}
                loaded={this.props.loaded}
                addPaymentMethod={this.addCreditCard}
                obligatory={false}
                isOnTrial={!this.props.team.billing.activated}
            /> : ""}

        </div>);
    }

};